/**
 * ClienteNav — sub-nav horizontal no Portal Cliente (B1.7).
 *
 * Renderiza links pras seções principais (Dashboard / Meus chamados /
 * Chamados / Orçamentos / Manutenções / Contratos / Financeiro) condicionalmente
 * baseado em `dashboard.permissoes` (feature flags por role).
 *
 * Portaria vê só: Dashboard / Meus chamados.
 * Gestor vê todos os links (sujeito a permissões UFP customizadas).
 *
 * Sticky abaixo do header, scroll horizontal em mobile (overflow-x-auto).
 *
 * NÃO REVERTER:
 *   - Backend é fonte da verdade pras permissões (visualizar link ≠ poder
 *     acessar; cada page tem seu próprio guard). Hide aqui é só UX.
 *   - NavLink (não Link) — pega activeClassName automático
 */

import { NavLink } from 'react-router-dom';

// LAYOUT-CANONICO: régua importada do arquivo do PageContainer (uso de CASCA).
import { REGUA_LARGURA, REGUA_PADDING_X } from '@/components/ui/PageContainer';
import { useClienteAuth } from '@/contexts/ClienteAuthContext';
import { useClienteDashboard } from '@/lib/cliente-portal-store';
import { cn } from '@/lib/cn';

interface NavItem {
  to: string;
  label: string;
  permission?: string; // chave em dashboard.permissoes; ausente = sempre visível
}

/**
 * FASE-C — "Financeiro" saiu do menu.
 *
 * A tela é um stub declarado pelo próprio backend (`GET /cliente/financeiro`
 * devolve `historico: []` e `stub: true`): o que ela mostrava eram dois números
 * que o cliente já vê em Contratos e no Início, embrulhados num aviso de "em
 * construção". Um item de menu chamado *Financeiro* promete boleto, vencimento
 * e comprovante — nada disso existe ainda. Menu não é vitrine de intenção:
 * enquanto a cobrança não estiver integrada, o item some.
 *
 * A ROTA continua no ar (quem tiver o link salvo recebe um aviso honesto em vez
 * de um 404) e a permissão `ver_financeiro` segue intacta. Pra devolver o item
 * ao menu quando a integração existir, basta `true` aqui.
 */
const FINANCEIRO_NO_MENU = false;

const NAV_ITEMS: NavItem[] = [
  { to: '/cliente/dashboard', label: 'Início' },
  { to: '/cliente/meus-chamados', label: 'Meus chamados' },
  { to: '/cliente/chamados', label: 'Todos chamados', permission: 'ver_todos_chamados' },
  // ⛔ "Orçamentos" saiu em 27/10/2026 (D76, Matheus: "orçamentos do comercial devem sair do portal"). O orçamento da
  // Manutenção é respondido pelo link do e-mail; o Portal não mostra orçamento nem proposta (D9).
  { to: '/cliente/manutencoes', label: 'Manutenções', permission: 'ver_manutencoes_agendadas' },
  // F3.1: gated por `ver_projetos` (que já embute a flag PORTAL_CLIENTE_PROJETOS_ENABLED).
  { to: '/cliente/projetos', label: 'Projetos', permission: 'ver_projetos' },
  { to: '/cliente/contratos', label: 'Contratos', permission: 'ver_contratos' },
  ...(FINANCEIRO_NO_MENU
    ? [{ to: '/cliente/financeiro', label: 'Financeiro', permission: 'ver_financeiro' }]
    : []),
  // B1.8
  { to: '/cliente/equipe', label: 'Equipe', permission: 'gerenciar_equipe' },
  { to: '/cliente/perfil', label: 'Perfil' },
];

export function ClienteNav() {
  const { isAuthenticated } = useClienteAuth();
  const dashQuery = useClienteDashboard();
  if (!isAuthenticated) return null;
  const permissoes = dashQuery.data?.permissoes ?? {};

  const items = NAV_ITEMS.filter((it) => !it.permission || permissoes[it.permission] === true);
  if (items.length <= 1) return null; // só Dashboard — não mostra nav

  return (
    <nav
      className="sticky top-[57px] z-[9] border-b border-line bg-paper-warm dark:border-line-dark dark:bg-paper-dark-warm md:top-[65px]"
      aria-label="Seções do portal"
      data-testid="cliente-nav"
    >
      {/* No 375px o menu rola na horizontal: o degradê à direita avisa que há
          mais itens (sem ele o síndico não descobre "Perfil" no fim da fila). */}
      {/* LAYOUT-CANONICO: o `max-w-6xl px-4` próprio saiu — a nav do portal
          alinha com o cabeçalho e com o conteúdo pela mesma régua. */}
      <div className={cn('relative', REGUA_LARGURA)}>
        <div
          className={cn(
            'overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            REGUA_PADDING_X,
          )}
        >
          <ul className="flex items-center gap-1 py-2">
            {items.map((it) => (
              <li key={it.to}>
                <NavLink
                  to={it.to}
                  className={({ isActive }) =>
                    `inline-block whitespace-nowrap rounded-pill px-3.5 py-2 text-sm transition ${
                      isActive
                        ? 'bg-red font-medium text-white'
                        : 'text-ink-2 hover:bg-paper-surface dark:text-ink-dark-2 dark:hover:bg-paper-dark-surface'
                    }`
                  }
                  data-testid={`cliente-nav-link-${it.to.split('/').pop()}`}
                >
                  {it.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-paper-warm to-transparent dark:from-paper-dark-warm md:hidden"
        />
      </div>
    </nav>
  );
}
